import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/apiClient'

// Define root URL for auth endpoints
const API_ROOT_URL = import.meta.env.VITE_HASMCP_ACCOUNTS_API_ROOT_URL;
const CACHE_TTL_MS = 3 * 60 * 60 * 1000 // 3 Hours

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('hasmcp_token') || '')
  const user = ref(null)
  const organizations = ref([])
  const selectedOrganizationId = ref(localStorage.getItem('hasmcp_active_org') || null)

  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!token.value)

  // --- Cache Helpers ---
  const saveToCache = (key, data) => {
    const payload = { value: data, expires: Date.now() + CACHE_TTL_MS }
    localStorage.setItem(key, JSON.stringify(payload))
  }

  const loadFromCache = (key) => {
    const itemStr = localStorage.getItem(key)
    if (!itemStr) return null
    try {
      const item = JSON.parse(itemStr)
      if (Date.now() > item.expires) {
        localStorage.removeItem(key)
        return null
      }
      return item.value
    } catch (e) {
      localStorage.removeItem(key)
      return null
    }
  }

  // --- Actions ---

  // Refreshes the Access Token using the HttpOnly cookie
  // MUST use native fetch to avoid infinite loops with apiClient interceptors
  const refreshAccessToken = async () => {
    try {
      const res = await fetch(`${API_ROOT_URL}/auth/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Send the _rt cookie
        body: JSON.stringify({}) // FIX: Payload must be empty object
      })

      if (res.ok) {
        const data = await res.json()
        // FIX: Server returns { "token": { "accessToken": "..." } }
        const newToken = data.token?.accessToken

        if (newToken) {
          token.value = newToken
          localStorage.setItem('hasmcp_token', newToken)
          return true
        }
      }
    } catch (e) {
      console.error('Token refresh failed:', e)
    }
    return false
  }

  const activeOrganization = computed(() => {
    if (selectedOrganizationId.value && organizations.value.length > 0) {
      return organizations.value.find(o => o.id === selectedOrganizationId.value) || organizations.value[0]
    }
    return organizations.value.length > 0 ? organizations.value[0] : null
  })

  const hasMultipleOrganizations = computed(() => {
    return organizations.value.length > 1
  })

  const initialize = async () => {
    const cachedUser = loadFromCache('hasmcp_user')
    if (cachedUser) {
      user.value = cachedUser
    } else {
      await fetchUser()
    }

    const cachedOrgs = loadFromCache('hasmcp_orgs')
    if (Array.isArray(cachedOrgs)) {
      organizations.value = cachedOrgs
    } else {
      await fetchOrganizations()
    }

    if (!selectedOrganizationId.value && Array.isArray(organizations.value) && organizations.value.length > 0) {
      setActiveOrganization(organizations.value[0].id)
    }
  }

  const setActiveOrganization = (orgId) => {
    const exists = Array.isArray(organizations.value) && organizations.value.some(o => o.id === orgId)
    if (exists) {
      selectedOrganizationId.value = orgId
      localStorage.setItem('hasmcp_active_org', orgId)
    } else if (orgId === null) {
      selectedOrganizationId.value = null
      localStorage.removeItem('hasmcp_active_org')
    }
  }

  const syncOrganizations = async (newOrgs) => {
    const orgsArray = Array.isArray(newOrgs) ? newOrgs : []
    organizations.value = orgsArray
    saveToCache('hasmcp_orgs', orgsArray)
  }

  const handleLoginCallback = async () => {
    const hash = window.location.hash
    if (hash && hash.includes('t=')) {
      const match = hash.match(/t=([^&]*)/)
      if (match && match[1]) {
        const accessToken = match[1]
        token.value = accessToken
        localStorage.setItem('hasmcp_token', accessToken)
        window.history.replaceState(null, '', window.location.pathname)
        await fetchUser()
        await fetchOrganizations()
        return true
      }
    }
    return false
  }

  const fetchUser = async () => {
    isLoading.value = true
    try {
      // Use shared apiClient
      const res = await api.get('/users/me')
      if (res && res.data) {
        const userData = res.data.user || res.data
        user.value = userData
        saveToCache('hasmcp_user', userData)
      }
    } catch (e) {
      console.error('failed to fetch user', e)
    } finally {
      isLoading.value = false
    }
  }

  const updateUser = async (name) => {
    isLoading.value = true
    try {
      const res = await api.patch('/users/me', { user: { name } })
      if (res && res.data) {
        user.value = { ...user.value, name }
        saveToCache('hasmcp_user', user.value)
        return true
      }
    } catch (e) {
      console.error('Update user failed:', e)
      throw e
    } finally {
      isLoading.value = false
    }
    return false
  }

  const deleteUser = async () => {
    isLoading.value = true
    try {
      const res = await api.delete('/users/me')
      if (res) {
        signout()
        return true
      }
    } catch (e) {
      console.error('Delete user failed:', e)
      throw e
    } finally {
      isLoading.value = false
    }
    return false
  }

  const fetchOrganizations = async () => {
    try {
      const res = await api.get('/users/me/organizations')
      if (res && res.data) {
        // Fix: res.data itself might be an object {} which is NOT the array we want.
        // We look for .organizations or .items, or check if .data itself IS the array.
        let orgs = res.data.organizations || res.data.items
        if (!orgs && Array.isArray(res.data)) {
          orgs = res.data
        }

        const orgsArray = Array.isArray(orgs) ? orgs : []
        organizations.value = orgsArray
        saveToCache('hasmcp_orgs', orgsArray)

        if (!selectedOrganizationId.value && orgsArray.length > 0) {
          setActiveOrganization(orgsArray[0].id)
        }
      }
    } catch (e) {
      console.error('failed to fetch organization', e)
    }
  }

  const login = (provider = 'github') => {
    window.location.href = `${API_ROOT_URL}/auth/oauth2/${provider}/authorize`
  }

  const signout = () => {
    token.value = ''
    user.value = null
    organizations.value = []
    selectedOrganizationId.value = null

    localStorage.removeItem('hasmcp_token')
    localStorage.removeItem('hasmcp_user')
    localStorage.removeItem('hasmcp_orgs')
    localStorage.removeItem('hasmcp_active_org')
  }

  return {
    token,
    user,
    organizations,
    activeOrganization,
    hasMultipleOrganizations,
    isAuthenticated,
    isLoading,
    initialize,
    setActiveOrganization,
    syncOrganizations,
    handleLoginCallback,
    fetchUser,
    updateUser,
    deleteUser,
    fetchOrganizations,
    refreshAccessToken,
    login,
    signout
  }
})